import React, { useState } from 'react';


const SECTIONS = [
  { id: 'overview', label: 'Overview', group: 'Getting started' },
  { id: 'quickstart', label: 'Quickstart', group: 'Getting started' },
  { id: 'auth', label: 'Authentication', group: 'Getting started' },
  { id: 'connect', label: 'Connect widget', group: 'Core APIs' },
  { id: 'accounts', label: 'Accounts', group: 'Core APIs' },
  { id: 'transactions', label: 'Transactions', group: 'Core APIs' },
  { id: 'identity', label: 'Identity / BVN', group: 'Core APIs' },
  { id: 'webhooks', label: 'Webhooks', group: 'Events' },
  { id: 'errors', label: 'Error handling', group: 'Reference' },
  { id: 'rate-limits', label: 'Rate limits', group: 'Reference' },
  { id: 'sandbox', label: 'Sandbox testing', group: 'Getting started' },
  { id: 'kyc', label: 'KYC verification', group: 'Identity' },
  { id: 'kyb', label: 'KYB (Business)', group: 'Identity' },
  { id: 'sanctions', label: 'AML / Sanctions', group: 'Identity' },
  { id: 'intelligence', label: 'Intelligence', group: 'AI' },
  { id: 'billing', label: 'Billing & Usage', group: 'Reference' },
];

const CodeBlock = ({ code, lang = 'bash' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-4 rounded-xl overflow-hidden border border-gray-800">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800">
        <span className="text-xs text-gray-500 font-mono">{lang}</span>
        <button onClick={handleCopy} className="text-xs text-gray-500 hover:text-gray-300 transition">
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="p-4 bg-[#0d1117] text-sm text-gray-300 overflow-x-auto font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const Endpoint = ({ method, path, desc }) => {
  const colors: Object = {
    GET: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    POST: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    DELETE: 'bg-red-500/10 text-red-400 border-red-500/20',
  };
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100 my-3">
      <span className={`px-2.5 py-1 text-xs font-bold rounded-md border ${colors[method] || 'bg-gray-100 text-gray-600'}`}>
        {method}
      </span>
      <div>
        <code className="text-sm font-mono text-gray-900">{path}</code>
        <p className="text-sm text-gray-500 mt-1">{desc}</p>
      </div>
    </div>
  );
};

const Param = ({ name, type, required, desc }) => (
  <tr className="border-b border-gray-100">
    <td className="py-3 pr-4">
      <code className="text-sm font-mono text-gray-900">{name}</code>
      {required && <span className="ml-2 text-[10px] text-red-400 font-medium">required</span>}
    </td>
    <td className="py-3 pr-4 text-sm text-gray-500 font-mono">{type}</td>
    <td className="py-3 text-sm text-gray-600">{desc}</td>
  </tr>
);

const DocsPage = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const groupedSections = SECTIONS.reduce((acc, s) => {
    if (!acc[s.group]) acc[s.group] = [];
    acc[s.group].push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" style={{textDecoration:"none",display:"flex",alignItems:"center",gap:"12px"}}>
              <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">AL</span>
              </div>
              <span className="text-lg font-semibold text-gray-900">AfriLink</span>
            </a>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium ml-1">Docs</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-gray-500 hover:text-gray-900 transition">Widget demo</a>
            <a href="/admin" className="text-sm text-gray-500 hover:text-gray-900 transition">Dashboard</a>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-white text-xs font-medium rounded-lg">
              v1.0
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar */}
        <nav className="w-60 flex-shrink-0 border-r border-gray-100 py-8 px-4 sticky top-[65px] h-[calc(100vh-65px)] overflow-y-auto">
          {Object.entries(groupedSections).map(([group, items]) => (
            <div key={group} className="mb-6">
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-2 px-3">{group}</p>
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition mb-0.5 ${
                    activeSection === item.id
                      ? 'bg-gray-900 text-white font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* Content */}
        <main className="flex-1 max-w-3xl py-10 px-12">
          {activeSection === 'overview' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">AfriLink API</h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Open banking infrastructure for Africa. Connect bank accounts, retrieve financial data,
                verify identities, and accept payments — all through a single API.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { title: 'Connect', desc: 'Embeddable widget for bank account linking', icon: '🔗' },
                  { title: 'Data', desc: 'Accounts, balances, and transactions', icon: '📊' },
                  { title: 'Identity', desc: 'BVN verification and KYC', icon: '🛡' },
                  { title: 'Webhooks', desc: 'Real-time event notifications', icon: '⚡' },
                ].map((item) => (
                  <div key={item.title} className="p-5 rounded-xl border border-gray-100 hover:border-gray-200 transition cursor-pointer"
                    onClick={() => setActiveSection(item.title.toLowerCase())}>
                    <span className="text-2xl mb-3 block">{item.icon}</span>
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                ))}
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Base URL</h2>
              <CodeBlock code="https://api.afrilink.io/api" lang="text" />
              <p className="text-sm text-gray-600">
                All API requests are made over HTTPS. Calls made over HTTP will be rejected.
              </p>
            </div>
          )}

          {activeSection === 'quickstart' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Quickstart</h1>
              <p className="text-gray-600 mb-6">Get up and running with AfriLink in under 5 minutes.</p>

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">1. Get your API keys</h2>
              <p className="text-gray-600 mb-3">
                Create an app in the AfriLink dashboard to get your public and secret key pair.
              </p>
              <CodeBlock lang="bash" code={`curl -X POST https://api.afrilink.io/api/apps \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "My App", "environment": "test"}'`} />
              <p className="text-sm text-gray-500 mb-6">
                Save your secret key immediately — it will only be shown once.
              </p>

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">2. Add the Connect widget</h2>
              <p className="text-gray-600 mb-3">
                Drop this script into your frontend to let users link their bank accounts.
              </p>
              <CodeBlock lang="html" code={`<script src="https://connect.afrilink.io/sdk.js"></script>
<script>
  const afrilink = new AfriLinkConnect({
    publicKey: 'pk_test_your_public_key',
    customer: {
      name: 'John Doe',
      email: 'john@example.com'
    },
    onSuccess: function(data) {
      console.log('Account linked:', data);
      // Send data.id to your backend
    },
    onClose: function() {
      console.log('Widget closed');
    }
  });

  // Call this when user clicks "Connect Bank"
  afrilink.open();
</script>`} />

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">3. Fetch financial data</h2>
              <p className="text-gray-600 mb-3">
                Use the account ID from the connect callback to retrieve data.
              </p>
              <CodeBlock lang="bash" code={`# Get account details
curl https://api.afrilink.io/api/data/accounts/ACCOUNT_ID \\
  -H "x-api-key: sk_test_your_secret_key"

# Get transactions
curl https://api.afrilink.io/api/data/accounts/ACCOUNT_ID/transactions \\
  -H "x-api-key: sk_test_your_secret_key"`} />

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">4. Listen for events</h2>
              <p className="text-gray-600 mb-3">
                Set up a webhook to receive real-time notifications.
              </p>
              <CodeBlock lang="bash" code={`curl -X POST https://api.afrilink.io/api/webhooks \\
  -H "x-api-key: sk_test_your_secret_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://yourapp.com/webhooks/afrilink",
    "events": ["account.connected", "account.updated"]
  }'`} />
            </div>
          )}

          {activeSection === 'auth' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Authentication</h1>
              <p className="text-gray-600 mb-6">
                AfriLink uses API keys to authenticate requests. You can manage your keys in the dashboard.
              </p>

              <h2 className="text-xl font-semibold text-gray-900 mb-3">Key types</h2>
              <table className="w-full text-left mb-8">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Key</th>
                    <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Prefix</th>
                    <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Usage</th>
                  </tr>
                </thead>
                <tbody>
                  <Param name="Public key" type="pk_test_... / pk_live_..." desc="Used in the Connect widget on your frontend. Safe to expose in client-side code." />
                  <Param name="Secret key" type="sk_test_... / sk_live_..." desc="Used for server-side API calls. Never expose in client-side code." />
                </tbody>
              </table>

              <h2 className="text-xl font-semibold text-gray-900 mb-3">Using your secret key</h2>
              <p className="text-gray-600 mb-3">
                Pass your secret key in the request header:
              </p>
              <CodeBlock lang="bash" code={`curl https://api.afrilink.io/api/data/accounts \\
  -H "x-api-key: sk_test_your_secret_key"`} />

              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 mt-6">
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">Security:</span> Never include your secret key in client-side code,
                  git repositories, or log files. If a key is compromised, rotate it immediately via the API.
                </p>
              </div>
            </div>
          )}

          {activeSection === 'connect' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Connect widget</h1>
              <p className="text-gray-600 mb-6">
                The Connect widget lets your users securely link their bank accounts. It handles bank selection,
                authentication, and consent — you receive the linked account ID.
              </p>

              <h2 className="text-xl font-semibold text-gray-900 mb-3">SDK configuration</h2>
              <CodeBlock lang="javascript" code={`const afrilink = new AfriLinkConnect({
  publicKey: 'pk_test_...',       // Required
  customer: {
    id: 'cust_abc123',            // Optional — reuse for returning customers
    name: 'Adebayo Okonkwo',     // Required if no id
    email: 'adebayo@example.com', // Required if no id
  },
  onSuccess: (data) => {
    // data = { id, userId, institution }
    // Send data.id to your server to fetch financial data
  },
  onClose: () => {
    // User closed the widget without completing
  },
  onEvent: (event) => {
    // event = { type: 'WIDGET_OPENED' | 'BANK_SELECTED' | ... }
  },
});

afrilink.open();`} />

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">Server-side endpoints</h2>
              <Endpoint method="POST" path="/api/connect/session" desc="Create a new connect session" />
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Parameter</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Description</th>
                </tr></thead>
                <tbody>
                  <Param name="userId" type="string" required desc="Your unique identifier for the user" />
                </tbody>
              </table>

              <Endpoint method="POST" path="/api/connect/complete" desc="Complete account linking after user auth" />
              <table className="w-full text-left">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Parameter</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Description</th>
                </tr></thead>
                <tbody>
                  <Param name="sessionId" type="string" required desc="Session ID from the session endpoint" />
                  <Param name="userId" type="string" required desc="Must match the session's user" />
                  <Param name="institution" type="string" required desc="Bank name selected by the user" />
                </tbody>
              </table>
            </div>
          )}

          {activeSection === 'accounts' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Accounts</h1>
              <p className="text-gray-600 mb-6">
                Retrieve details about linked bank accounts, including balances, account holder information,
                and account status.
              </p>

              <Endpoint method="GET" path="/api/data/accounts" desc="List all linked accounts for a user" />
              <CodeBlock lang="bash" code={`curl "https://api.afrilink.io/api/data/accounts?userId=user_123" \\
  -H "x-api-key: sk_test_..."`} />
              <CodeBlock lang="json" code={`{
  "status": "successful",
  "data": [
    {
      "id": "clxyz123...",
      "institution": "GTBank",
      "accountName": "Adebayo Okonkwo",
      "accountNumber": "******6789",
      "balance": 245000.75,
      "currency": "NGN",
      "status": "LINKED",
      "linkedAt": "2026-03-29T12:00:00.000Z"
    }
  ]
}`} />

              <Endpoint method="GET" path="/api/data/accounts/:id" desc="Get details for a specific linked account" />
              <Endpoint method="GET" path="/api/data/accounts/:id/balance" desc="Get current account balance" />
              <Endpoint method="DELETE" path="/api/data/accounts/:id" desc="Unlink an account (revoke consent)" />
            </div>
          )}

          {activeSection === 'transactions' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Transactions</h1>
              <p className="text-gray-600 mb-6">
                Retrieve transaction history for a linked account. Supports filtering by date range,
                type (credit/debit), and pagination.
              </p>

              <Endpoint method="GET" path="/api/data/accounts/:id/transactions" desc="Get paginated transactions" />
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Query param</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Description</th>
                </tr></thead>
                <tbody>
                  <Param name="start" type="ISO date" desc="Filter transactions from this date" />
                  <Param name="end" type="ISO date" desc="Filter transactions up to this date" />
                  <Param name="type" type="credit | debit" desc="Filter by transaction type" />
                  <Param name="limit" type="number" desc="Results per page (default: 50, max: 100)" />
                  <Param name="page" type="number" desc="Page number (default: 1)" />
                </tbody>
              </table>

              <CodeBlock lang="bash" code={`curl "https://api.afrilink.io/api/data/accounts/clxyz.../transactions\\
  ?start=2026-01-01&end=2026-03-31&type=credit&limit=20" \\
  -H "x-api-key: sk_test_..."`} />
              <CodeBlock lang="json" code={`{
  "status": "successful",
  "data": [
    {
      "id": "txn_abc...",
      "type": "credit",
      "amount": 50000,
      "currency": "NGN",
      "description": "Salary deposit",
      "status": "COMPLETED",
      "date": "2026-03-28T09:00:00.000Z"
    }
  ],
  "meta": {
    "total": 142,
    "page": 1,
    "limit": 20,
    "pages": 8
  }
}`} />
            </div>
          )}

          {activeSection === 'identity' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Identity / BVN</h1>
              <p className="text-gray-600 mb-6">
                Verify a user's identity using their Bank Verification Number (BVN).
                Cross-reference with linked accounts for name matching.
              </p>

              <Endpoint method="POST" path="/api/identity/verify" desc="Verify a BVN" />
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Parameter</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Description</th>
                </tr></thead>
                <tbody>
                  <Param name="bvn" type="string" required desc="11-digit Bank Verification Number" />
                  <Param name="accountId" type="string" desc="Optional linked account ID for name matching" />
                </tbody>
              </table>
              <CodeBlock lang="json" code={`{
  "status": "successful",
  "data": {
    "verified": true,
    "firstName": "Adebayo",
    "lastName": "Okonkwo",
    "dateOfBirth": "1990-05-15",
    "phoneNumber": "+234****678",
    "gender": "Male",
    "enrollmentBank": "GTBank",
    "nameMatch": true
  }
}`} />

              <Endpoint method="GET" path="/api/identity/status/:accountId" desc="Check BVN status for a linked account" />
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 mt-4">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Test BVNs:</span> Use <code className="bg-blue-100 px-1 rounded">22211122233</code> or <code className="bg-blue-100 px-1 rounded">33322233344</code> in test mode.
                </p>
              </div>
            </div>
          )}

          {activeSection === 'webhooks' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Webhooks</h1>
              <p className="text-gray-600 mb-6">
                Receive real-time notifications when events happen on linked accounts. AfriLink signs
                every webhook with HMAC-SHA256 so you can verify authenticity.
              </p>

              <h2 className="text-xl font-semibold text-gray-900 mb-3">Event types</h2>
              <table className="w-full text-left mb-8">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Event</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Description</th>
                </tr></thead>
                <tbody>
                  <Param name="account.connected" type="" desc="A user successfully linked their bank account" />
                  <Param name="account.updated" type="" desc="Account data has been refreshed" />
                  <Param name="account.revoked" type="" desc="A user or your app revoked access" />
                  <Param name="identity.verified" type="" desc="BVN verification completed" />
                  <Param name="data.available" type="" desc="Financial data is ready for retrieval" />
                </tbody>
              </table>

              <Endpoint method="POST" path="/api/webhooks" desc="Create a webhook subscription" />
              <CodeBlock lang="bash" code={`curl -X POST https://api.afrilink.io/api/webhooks \\
  -H "x-api-key: sk_test_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://yourapp.com/hooks/afrilink",
    "events": ["account.connected", "account.revoked"]
  }'`} />

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">Verifying signatures</h2>
              <p className="text-gray-600 mb-3">
                Every webhook includes an <code className="bg-gray-100 px-1 rounded text-sm">X-AfriLink-Signature</code> header.
                Verify it against the payload using your webhook secret:
              </p>
              <CodeBlock lang="javascript" code={`const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(JSON.stringify(payload))
    .digest('hex');
  return signature === expected;
}

// In your Express handler:
app.post('/hooks/afrilink', (req, res) => {
  const sig = req.headers['x-afrilink-signature'];
  if (!verifyWebhook(req.body, sig, WEBHOOK_SECRET)) {
    return res.status(401).send('Invalid signature');
  }
  // Process the event
  console.log(req.body.event, req.body.data);
  res.status(200).send('OK');
});`} />

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">Retry policy</h2>
              <p className="text-gray-600">
                Failed deliveries are retried with exponential backoff: 1 min, 5 min, 30 min, 2 hours, 24 hours.
                After 5 failed attempts, the delivery is marked failed.
              </p>
            </div>
          )}

          {activeSection === 'errors' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Error handling</h1>
              <p className="text-gray-600 mb-6">
                AfriLink returns standard HTTP status codes. Errors include a JSON body with details.
              </p>
              <table className="w-full text-left">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Code</th>
                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Meaning</th>
                </tr></thead>
                <tbody>
                  <Param name="200" type="" desc="Request succeeded" />
                  <Param name="201" type="" desc="Resource created successfully" />
                  <Param name="400" type="" desc="Bad request — check the error message and validation details" />
                  <Param name="401" type="" desc="No API key provided" />
                  <Param name="403" type="" desc="Invalid or revoked API key" />
                  <Param name="404" type="" desc="Resource not found" />
                  <Param name="429" type="" desc="Rate limit exceeded — see rate limits section" />
                  <Param name="500" type="" desc="Internal server error — contact support" />
                </tbody>
              </table>
              <CodeBlock lang="json" code={`{
  "error": "Validation failed",
  "details": [
    { "field": "bvn", "message": "BVN must be exactly 11 digits" }
  ]
}`} />
            </div>
          )}

          {activeSection === 'rate-limits' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Rate limits</h1>
              <p className="text-gray-600 mb-6">
                Rate limits are applied per API key based on your environment.
              </p>
              <table className="w-full text-left mb-8">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Environment</th>
                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Limit</th>
                  <th className="pb-3 text-xs font-semibold text-gray-500 uppercase">Window</th>
                </tr></thead>
                <tbody>
                  <Param name="Test" type="30 requests" desc="Per minute" />
                  <Param name="Live" type="120 requests" desc="Per minute" />
                </tbody>
              </table>

              <h2 className="text-xl font-semibold text-gray-900 mb-3">Response headers</h2>
              <p className="text-gray-600 mb-3">Every response includes rate limit headers:</p>
              <table className="w-full text-left mb-6">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Header</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Description</th>
                </tr></thead>
                <tbody>
                  <Param name="X-RateLimit-Limit" type="" desc="Your limit for the current window" />
                  <Param name="X-RateLimit-Remaining" type="" desc="Requests remaining in this window" />
                  <Param name="X-RateLimit-Reset" type="" desc="Unix timestamp when the window resets" />
                </tbody>
              </table>

              <CodeBlock lang="json" code={`// 429 response when limit is exceeded:
{
  "error": "Rate limit exceeded",
  "limit": 30,
  "windowMs": 60000,
  "retryAfter": 45
}`} />
            </div>
          )}

          {activeSection === 'sandbox' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Sandbox testing</h1>
              <p className="text-gray-600 mb-6">
                AfriLink runs in sandbox mode by default. All test data is pre-seeded so you can build and test your integration without connecting to real banks.
              </p>

              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 mb-8">
                <p className="text-sm text-emerald-800">
                  <span className="font-semibold">No charges in sandbox.</span> All API calls in test mode are free. Switch to live mode only when you are ready to process real data.
                </p>
              </div>

              <h2 className="text-xl font-semibold text-gray-900 mb-3">How sandbox works</h2>
              <p className="text-gray-600 mb-4">
                When your app uses test keys (pk_test_... / sk_test_...), all data comes from AfriLink's mock provider. No real bank connections are made. The mock provider returns realistic Nigerian bank data including account details, 20 seeded transactions, and balances.
              </p>

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">Step 1: Create a test app</h2>
              <p className="text-gray-600 mb-3">Register an account and create an app in the dashboard. Your test keys are generated automatically.</p>
              <CodeBlock lang="bash" code="curl -X POST https://api.afrilink.io/api/auth/register -H Content-Type:application/json -d {email:dev@yourco.com, password:securepass123}" />

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">Step 2: Link a test account</h2>
              <p className="text-gray-600 mb-3">Use any of these test banks. Each returns different mock data.</p>
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Bank name</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Test balance</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Account name</th>
                </tr></thead>
                <tbody>
                  <Param name="GTBank" type="NGN 245,000.75" desc="Adebayo Okonkwo" />
                  <Param name="Zenith Bank" type="NGN 1,320,500.00" desc="Adebayo Okonkwo" />
                  <Param name="Access Bank" type="NGN 89,750.50" desc="Adebayo Okonkwo" />
                  <Param name="First Bank" type="NGN 560,000.00" desc="Adebayo Okonkwo" />
                  <Param name="UBA" type="NGN 178,300.25" desc="Adebayo Okonkwo" />
                </tbody>
              </table>

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">Step 3: Test identity verification</h2>
              <p className="text-gray-600 mb-3">Use these test credentials to simulate BVN, NIN, and CAC lookups.</p>

              <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Test BVNs</h3>
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">BVN</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Result</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Name returned</th>
                </tr></thead>
                <tbody>
                  <Param name="22211122233" type="Verified" desc="Adebayo Okonkwo" />
                  <Param name="33322233344" type="Verified" desc="Amina Ibrahim" />
                  <Param name="44433344455" type="Verified" desc="Chinedu Eze" />
                  <Param name="00000000000" type="Not found" desc="Returns verification failed" />
                </tbody>
              </table>

              <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Test NINs</h3>
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">NIN</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Result</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Name returned</th>
                </tr></thead>
                <tbody>
                  <Param name="12345678901" type="Verified" desc="Adebayo Okonkwo" />
                  <Param name="98765432109" type="Verified" desc="Amina Ibrahim" />
                </tbody>
              </table>

              <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Test CAC (Business)</h3>
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">RC number</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Company</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Type</th>
                </tr></thead>
                <tbody>
                  <Param name="RC1234567" type="AfriTech Solutions Ltd" desc="LLC - Active" />
                  <Param name="RC9876543" type="Sahel Agro Industries Plc" desc="PLC - Active" />
                  <Param name="BN3456789" type="Ngozi Fashion House" desc="BN - Active" />
                </tbody>
              </table>

              <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Test TINs</h3>
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">TIN</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Taxpayer</th>
                </tr></thead>
                <tbody>
                  <Param name="12345678-0001" type="AfriTech Solutions Ltd" desc="" />
                  <Param name="98765432-0001" type="Sahel Agro Industries Plc" desc="" />
                </tbody>
              </table>

              <h3 className="text-base font-semibold text-gray-900 mb-2 mt-6">Test sanctions screening</h3>
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 mb-4">
                <p className="text-sm text-amber-800">
                  Any name containing <code className="bg-amber-100 px-1 rounded">"Magu"</code> will trigger a sanctions match (PEP - HIGH risk). All other names return clear.
                </p>
              </div>

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">Step 4: Test seeded transactions</h2>
              <p className="text-gray-600 mb-3">Every test account link creates 20 realistic Nigerian bank transactions including:</p>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {['Salary deposits', 'MTN Airtime VTU', 'Uber/Bolt rides', 'DStv subscription', 'POS purchases (Shoprite)', 'EKEDC prepaid meter', 'ATM withdrawals', 'Piggyvest transfers', 'Loan repayments (Carbon)', 'Stamp duty charges'].map(function(t) { return (
                  <div key={t} className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-emerald-500 text-xs">&#10003;</span> {t}
                  </div>
                ); })}
              </div>

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">Step 5: Run the E2E test agent</h2>
              <p className="text-gray-600 mb-3">Validate your entire integration with our 40-test automated suite:</p>
              <CodeBlock lang="bash" code="node test-agent.js https://api.afrilink.io" />
              <p className="text-sm text-gray-500">Tests auth, apps, connect, data, identity, billing, webhooks, admin, and edge cases in 7 seconds.</p>

              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-8">Going live</h2>
              <p className="text-gray-600 mb-3">When you are ready for real bank connections, set these environment variables:</p>
              <CodeBlock lang="bash" code={"BANK_PROVIDER=mono\nMONO_SECRET_KEY=live_sk_...\nIDENTITY_PROVIDER=smile_id\nSMILE_ID_PARTNER_ID=...\nSMILE_ID_API_KEY=..."} />
              <CodeBlock lang="bash" code="curl -X POST https://api.afrilink.io/api/identity/bvn -H x-api-key:sk_test_... -H Content-Type:application/json -d {bvn:22211122233}" />

              <Endpoint method="POST" path="/api/identity/nin" desc="Verify NIN or Virtual NIN" />
              <CodeBlock lang="bash" code="curl -X POST https://api.afrilink.io/api/identity/nin -H x-api-key:sk_test_... -H Content-Type:application/json -d {nin:12345678901}" />

              <Endpoint method="POST" path="/api/identity/document" desc="Verify passport, driver's license, voter's card, or national ID" />
              <Endpoint method="POST" path="/api/identity/biometric" desc="Selfie liveness check + face match against ID photo" />
              <Endpoint method="POST" path="/api/identity/address" desc="Physical address verification" />

              <Endpoint method="POST" path="/api/identity/kyc" desc="Composite full KYC (runs all applicable checks)" />
              <p className="text-gray-600 mb-3">The composite endpoint runs BVN + NIN + document + biometric + sanctions screening in one call. Returns a tiered result:</p>
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Tier</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Requirements</th>
                </tr></thead>
                <tbody>
                  <Param name="TIER_1" type="BVN or NIN verified" desc="Basic identity confirmation" />
                  <Param name="TIER_2" type="+ Document verified" desc="Enhanced identity with document" />
                  <Param name="TIER_3" type="+ Biometric verified" desc="Full identity with liveness check" />
                </tbody>
              </table>
            </div>
          )}

          {activeSection === 'kyb' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">KYB (Business verification)</h1>
              <p className="text-gray-600 mb-6">Verify Nigerian businesses using CAC registration, TIN, and director identity checks.</p>

              <Endpoint method="POST" path="/api/identity/cac" desc="Verify CAC registration (RC/BN number)" />
              <CodeBlock lang="bash" code="curl -X POST https://api.afrilink.io/api/identity/cac -H x-api-key:sk_test_... -H Content-Type:application/json -d {rcNumber:RC1234567}" />
              <p className="text-gray-600 mb-6">Returns company name, registration date, status, type, address, directors, and shareholders.</p>

              <Endpoint method="POST" path="/api/identity/tin" desc="Verify Tax Identification Number" />
              <Endpoint method="POST" path="/api/identity/directors" desc="Bulk-verify director IDs" />

              <Endpoint method="POST" path="/api/identity/kyb" desc="Composite full KYB (CAC + TIN + directors + sanctions)" />
            </div>
          )}

          {activeSection === 'sanctions' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">AML / Sanctions screening</h1>
              <p className="text-gray-600 mb-6">Screen individuals and businesses against 1,100+ global sanctions, PEP, and adverse media watchlists.</p>

              <Endpoint method="POST" path="/api/identity/sanctions" desc="Screen a name against global watchlists" />
              <CodeBlock lang="bash" code="curl -X POST https://api.afrilink.io/api/identity/sanctions -H x-api-key:sk_test_... -H Content-Type:application/json -d {fullName:Adebayo Okonkwo, entityType:INDIVIDUAL}" />
              <CodeBlock lang="json" code='{"status":"successful","data":{"matched":false,"riskLevel":"NONE","matches":[]}}' />

              <p className="text-gray-600 mb-3 mt-6">Risk levels returned:</p>
              <table className="w-full text-left mb-4">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Level</th>
                  <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Meaning</th>
                </tr></thead>
                <tbody>
                  <Param name="NONE" type="" desc="No matches found" />
                  <Param name="MEDIUM" type="" desc="Watchlist match (non-sanctions)" />
                  <Param name="HIGH" type="" desc="PEP (Politically Exposed Person)" />
                  <Param name="CRITICAL" type="" desc="Sanctions list match" />
                </tbody>
              </table>
            </div>
          )}

          {activeSection === 'intelligence' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Intelligence</h1>
              <p className="text-gray-600 mb-6">AI-powered financial insights built on transaction data. Includes credit scoring, fraud detection, income analysis, and transaction categorization.</p>

              <Endpoint method="GET" path="/api/intelligence/:accountId/insights" desc="Full account insights (all intelligence combined)" />
              <Endpoint method="GET" path="/api/intelligence/:accountId/categorize" desc="Categorize transactions (25+ Nigerian-specific categories)" />
              <p className="text-gray-600 mb-3">Categories include: SALARY, AIRTIME, TRANSPORT, FUEL, FOOD_GROCERY, RENT, UTILITIES, POS_PURCHASE, ATM_WITHDRAWAL, LOAN_REPAYMENT, BANK_CHARGE, and more.</p>

              <Endpoint method="GET" path="/api/intelligence/:accountId/income" desc="Income analysis (salary, freelance, pension detection)" />
              <Endpoint method="GET" path="/api/intelligence/:accountId/credit" desc="Cash-flow credit score (0-100)" />
              <Endpoint method="GET" path="/api/intelligence/:accountId/fraud" desc="Fraud risk assessment" />
              <Endpoint method="GET" path="/api/intelligence/:accountId/anomalies" desc="Anomaly detection on transactions" />
            </div>
          )}

          {activeSection === 'billing' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Billing and usage</h1>
              <p className="text-gray-600 mb-6">Track your API usage, check quotas, and manage your subscription plan.</p>

              <Endpoint method="GET" path="/api/billing/plans" desc="List all available plans (no auth required)" />
              <CodeBlock lang="json" code='[{"name":"starter","apiCallLimit":500,"price":"Free"},{"name":"growth","apiCallLimit":10000,"price":"NGN 25,000/mo"},{"name":"scale","apiCallLimit":100000,"price":"NGN 100,000/mo"},{"name":"enterprise","apiCallLimit":1000000,"price":"Custom"}]' />

              <Endpoint method="GET" path="/api/billing/usage" desc="Get current usage and remaining quota" />
              <CodeBlock lang="bash" code="curl https://api.afrilink.io/api/billing/usage -H x-api-key:sk_test_..." />

              <Endpoint method="POST" path="/api/billing/plan" desc="Change your subscription plan" />
              <p className="text-gray-600 mt-4">When you exceed your plan quota, API calls return a 429 status with upgrade instructions.</p>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default DocsPage;
